import { HttpHeaders } from '@angular/common/http';
import { Injectable } from '@angular/core';
import {
  AssetType,
  IAddresses,
  IAlertBrokerOfEndorsement,
  IAPIQuoteConvertResponse,
  IBank,
  IBankBranches,
  IBankingDetails,
  ICasaDetails,
  IInsuredAsset,
  IInsurersDetails,
  IPremiaBankingDetails,
  IPremiaLOV,
  IQuote,
  IQuoteConvertResponse,
  IQuoteErrorRepsonse,
  IQuoteReject,
  IQuoteRejectReason,
  IQuoteRequest,
  ISchedule,
  IScheduleInsuredAssets,
  ISpecialConditionAssets,
  PremiaChannel,
  QuoteRequestStatusType,
  SharedConfig,
} from '@takectrl-multiapp/svcs-share-config';
import { Observable, of, take, timer } from 'rxjs';
import { finalize, shareReplay, switchMap, tap } from 'rxjs/operators';
import { ICtrlResponse } from '../interfaces/api.interface';
import { APIAssetService } from './api-asset.service';
import { ApiCacheService } from './api-cache.service';
import { APIHttpService } from './api-http.service';
import { APIPolicyService } from './api-policy.service';
import { APIUserService } from './api-user.service';

@Injectable({
  providedIn: 'root',
})
export class APIQuoteScheduleService {
  private readonly CACHE_KEYS = {
    ALL_SCHEDULES: 'all_schedules',
    INSURERS: 'insurers',
    SPECIAL_CONDITIONS: 'special_conditions',
    BANK_BRANCHES: 'bank_branches',
    BANKS: 'banks',
    PREMIA_BANKS: 'premia_banks',
    PREMIA_ACCOUNT_TYPES: 'premia_account_types',
    PREMIA_BANK_RELATIONSHIP_TYPES: 'premia_bank_relationship_types',
    CASA_LIST: 'casa_list',
    QUOTE_REQUEST: 'quote_request',
    QUOTE_REQUESTS: 'quote_requests',
  };
  private scheduleRequestInProgress: Observable<ICtrlResponse<Array<ISchedule>>> | null = null;
  private readonly delayBetweenRequests = 1000;
  constructor(
    public apiUserService: APIUserService,
    public sharedConfig: SharedConfig,
    public apiPolicyService: APIPolicyService,
    public apiAssetService: APIAssetService,
    private readonly ctrlHttp: APIHttpService,
    private readonly cacheService: ApiCacheService
  ) {}

  getAllSchedules(): Observable<ICtrlResponse<Array<ISchedule>>> {
    // If a debounced (in-flight) request already exists, return it.
    if (this.scheduleRequestInProgress) {
      console.warn('There is already a /schedules/ request in flight. Returning the same request.');
      return this.scheduleRequestInProgress;
    }
    // Otherwise, start a new debounced request.
    this.scheduleRequestInProgress = timer(this.delayBetweenRequests).pipe(
      // After the debounce period, trigger the HTTP GET call.
      switchMap(() => this.ctrlHttp.get(`/schedules/`, { all_related: true, status: 'ALL' })),
      tap((response: ICtrlResponse<Array<ISchedule>>) => {
        // Cache the result
        this.cacheService.set(this.CACHE_KEYS.ALL_SCHEDULES, response, 5);
      }),
      // When the request completes (or errors), clear the in-flight reference.
      finalize(() => {
        this.scheduleRequestInProgress = null;
      }),
      // Ensure all subscribers share the same HTTP call and result.
      shareReplay(1)
    );
    return this.scheduleRequestInProgress;
  }

  premiumCheck(): Observable<ICtrlResponse<unknown>> {
    // No caching for POST operations
    return this.ctrlHttp.post(`/providers/brolink/policies/update_premiums/`, {});
  }


  getQuoteErrorMessage(quote_request_id: number, insurer_id: string): Observable<ICtrlResponse<IQuoteErrorRepsonse | {}>> {
    return this.ctrlHttp.get(`/quote-requests/${quote_request_id}/errors/${insurer_id}/`)
  }

  getDeclineQuoteReasons(): Observable<ICtrlResponse<Array<IQuoteRejectReason>>> {
    return this.ctrlHttp.get(`/providers/premia/values/quote_rejection_reason/`, {}, new HttpHeaders({ 'x-channel-key': PremiaChannel.APP }));
  }

  rejectQuote(payload: IQuoteReject): Observable<ICtrlResponse<unknown>> {
    return this.ctrlHttp.post(`/providers/premia/quote/reject/`, payload, new HttpHeaders({ 'x-channel-key': PremiaChannel.APP }));
  }

  getQuoteSchedule(quote_id: number): Observable<Blob> {
    return this.ctrlHttp.post(`/providers/premia/documents/quote/`, {quote_id: quote_id}, new HttpHeaders({ 'x-channel-key': PremiaChannel.APP }), undefined, 'blob');
  }

  getPremiaPolicySchedule(schedule_id: number): Observable<Blob> {
    return this.ctrlHttp.post(`/providers/premia/documents/policy/`, {schedule_id: schedule_id}, new HttpHeaders({ 'x-channel-key': PremiaChannel.APP }), undefined, 'blob');
  }

  getInsurers(): Observable<ICtrlResponse<Array<IInsurersDetails>>> {
    const cached = this.cacheService.get(this.CACHE_KEYS.INSURERS);
    if (cached) {
      return of(cached);
    }

    return this.ctrlHttp.get(`/insurers/`).pipe(tap((response) => this.cacheService.set(this.CACHE_KEYS.INSURERS, response, 60)));
  }

  submitKingPriceConfirmation(quoteRequestID: number): Observable<ICtrlResponse<unknown>> {
    this.invalidateQuoteRequestCaches(quoteRequestID);
    return this.ctrlHttp.post('/quotes/activate/king_price/' + quoteRequestID + '/', {});
  }

  initiateQuoteRequestProcessing(quoteRequestID: number): Observable<ICtrlResponse<any>> {
    this.invalidateQuoteRequestCaches(quoteRequestID);
    return this.ctrlHttp.post('/request-quotes/' + quoteRequestID + '/', {});
  }

  initiateEndorsementRequestProcessing(quoteRequestID: number): Observable<ICtrlResponse<any>> {
    this.invalidateQuoteRequestCaches(quoteRequestID);
    return this.ctrlHttp.post('/endorsements-submit/' + quoteRequestID + '/', {});
  }

  patchQuoteRequest(requestPayload: IQuoteRequest): Observable<ICtrlResponse<IQuoteRequest>> {
    this.invalidateQuoteRequestCaches(requestPayload.id);
    return this.ctrlHttp.patch(`/quote-requests/${requestPayload.id}/`, requestPayload);
  }

  endorsementUpdateAlertBroker(alertBrokerPayload: IAlertBrokerOfEndorsement): Observable<ICtrlResponse<IAlertBrokerOfEndorsement>> {
    return this.ctrlHttp.post('/endorsements-submit/request_update/', { ...alertBrokerPayload });
  }

  createNewQuoteRequest(requestPayload: object): Observable<ICtrlResponse<IQuoteRequest>> {
    this.cacheService.clear(this.CACHE_KEYS.QUOTE_REQUESTS);
    return this.ctrlHttp.post(`/quote-requests/`, requestPayload);
  }

  setQuoteRequest(quoteRequest: IQuoteRequest): Observable<ICtrlResponse<IQuoteRequest>> {
    this.invalidateQuoteRequestCaches(quoteRequest.id);
    return this.ctrlHttp.patch('/quote-requests/' + quoteRequest.id + '/', quoteRequest);
  }

  getQuoteRequestById(quote_id: number): Observable<ICtrlResponse<IQuoteRequest>> {
    return this.ctrlHttp.get('/quote-requests/' + quote_id + '/?related=true');
  }

  getQuoteRequests(): Observable<ICtrlResponse<IQuoteRequest[]>> {
    return this.ctrlHttp.get('/quote-requests/');
  }

  brolinkConvert(quoteRequestID: number, quote_nr: string, bankingDetails: IBankingDetails): Observable<ICtrlResponse<IQuoteConvertResponse>> {
    this.invalidateQuoteRequestCaches(quoteRequestID);
    return this.ctrlHttp.post('/quotes/put/convert/brolink/' + quoteRequestID + '/' + quote_nr + '/', bankingDetails);
  }

  premiaBankingDetails(bankingDetails: IPremiaBankingDetails): Observable<ICtrlResponse<IQuoteConvertResponse>> {
    return this.ctrlHttp.post('/providers/premia/clients/banking_details/', bankingDetails, new HttpHeaders({ 'x-channel-key': PremiaChannel.APP }));
  }

  premiaConvert(quote_id: number): Observable<ICtrlResponse<IQuoteConvertResponse>> {
    this.invalidateQuoteRequestCaches(quote_id);
    return this.ctrlHttp.post('/providers/premia/quote/accept/', {quote_id: quote_id}, new HttpHeaders({ 'x-channel-key': PremiaChannel.APP }))
  }

  santamDirectConvert(quoteRequestID: number): Observable<ICtrlResponse<IQuoteConvertResponse>> {
    this.invalidateQuoteRequestCaches(quoteRequestID);
    return this.ctrlHttp.post(`/quotes/converts/santamdirect/`, {
      quote_request_id: quoteRequestID,
      account_holder: SharedConfig.quoteBankingDetails.account_holder,
      account_number: SharedConfig.quoteBankingDetails.account_number,
      branch_code: SharedConfig.quoteBankingDetails.branch_code,
      branch_name: SharedConfig.quoteBankingDetails.branch_name,
      collection_day: SharedConfig.quoteBankingDetails.collection_day,
    });
  }

  acceptEndorsement(quoteRequestID: number): Observable<ICtrlResponse<String>> {
    this.invalidateQuoteRequestCaches(quoteRequestID);
    return this.ctrlHttp.post('/endorsements-accept/' + quoteRequestID + '/accepted/', {});
  }

  declineEndorsement(quoteRequestID: number): Observable<ICtrlResponse<String>> {
    this.invalidateQuoteRequestCaches(quoteRequestID);
    return this.ctrlHttp.post('/endorsements-accept/' + quoteRequestID + '/declined/', {});
  }

  miwayConvert(quoteRequestID: number): Observable<ICtrlResponse<IAPIQuoteConvertResponse>> {
    this.invalidateQuoteRequestCaches(quoteRequestID);
    return this.ctrlHttp.post(`/quotes/converts/miway/`, {
      user_id: this.sharedConfig.user.id,
      quote_request_id: quoteRequestID,
      account_holder: SharedConfig.quoteBankingDetails.account_holder,
      account_number: SharedConfig.quoteBankingDetails.account_number,
      branch_code: SharedConfig.quoteBankingDetails.branch_code,
      branch_name: SharedConfig.quoteBankingDetails.branch_name,
      collection_day: SharedConfig.quoteBankingDetails.collection_day,
    });
  }

  getQuote(user_id: string): Observable<ICtrlResponse<IQuote>> {
    const cacheKey = `quote_${user_id}`;
    const cached = this.cacheService.get(cacheKey);
    if (cached) {
      return of(cached);
    }

    return this.ctrlHttp.get(`/quotes/${user_id}`).pipe(tap((response) => this.cacheService.set(cacheKey, response, 5)));
  }

  getSpecialConditions(): Observable<ICtrlResponse<ISpecialConditionAssets>> {
    const cached = this.cacheService.get(this.CACHE_KEYS.SPECIAL_CONDITIONS);
    if (cached) {
      return of(cached);
    }

    return this.ctrlHttp.get(`/special-conditions/`).pipe(tap((response) => this.cacheService.set(this.CACHE_KEYS.SPECIAL_CONDITIONS, response, 60)));
  }

  setUnderwritingQuestionsCompleted(quote_request: IQuoteRequest): Observable<ICtrlResponse<IQuoteRequest>> {
    quote_request.underwriting_questions_completed = new Date().getTime();
    return this.setQuoteRequest(quote_request);
  }

  setAddress(address: IAddresses): Observable<ICtrlResponse<IAddresses>> {
    this.invalidateAddressRelatedCaches();
    return this.ctrlHttp.post(`/addresses/`, address);
  }

  updateAddress(address: IAddresses): Observable<ICtrlResponse<IAddresses>> {
    this.invalidateAddressRelatedCaches();
    return this.ctrlHttp.patch(`/addresses/${address.id}/`, address);
  }

  setOptionalsCompleted(quote_request: IQuoteRequest): Observable<ICtrlResponse<IQuoteRequest>> {
    quote_request.optionals_completed = new Date().getTime();
    return this.setQuoteRequest(quote_request);
  }

  setConfirmationCompleted(quote_request: IQuoteRequest): Observable<ICtrlResponse<IQuoteRequest>> {
    quote_request.status = QuoteRequestStatusType.Submitted;
    quote_request.confirmation_completed = new Date().getTime();
    return this.setQuoteRequest(quote_request);
  }

  updateQuoteRequestStatus(quote_request: IQuoteRequest, status: QuoteRequestStatusType): Observable<ICtrlResponse<IQuoteRequest>> {
    quote_request.status = status;
    return this.setQuoteRequest(quote_request);
  }

  updateCoverStartDate(date: string, quote_request: IQuoteRequest): Observable<ICtrlResponse<IQuoteRequest>> {
    quote_request.cover_start_date = new Date(date);
    return this.setQuoteRequest(quote_request);
  }

  getBankBranches(bank: string): Observable<ICtrlResponse<IBankBranches>> {
    const cacheKey = `${this.CACHE_KEYS.BANK_BRANCHES}_${bank}`;
    const cached = this.cacheService.get(cacheKey);
    if (cached) {
      return of(cached);
    }

    return this.ctrlHttp.get(`/bankbranchcodes/${bank}`).pipe(tap((response) => this.cacheService.set(cacheKey, response, 1440)));
  }

  getBanks(): Observable<ICtrlResponse<IBank[]>> {
    const cached = this.cacheService.get(this.CACHE_KEYS.BANKS);
    if (cached) {
      return of(cached);
    }

    return this.ctrlHttp.get(`/banks/`).pipe(tap((response) => this.cacheService.set(this.CACHE_KEYS.BANKS, response, 1440)));
  }

  getPremiaBanks(): Observable<ICtrlResponse<IPremiaLOV[]>> {
    const cached = this.cacheService.get(this.CACHE_KEYS.PREMIA_BANKS);
    if (cached) {
      return of(cached);
    }

    return this.ctrlHttp
      .get<ICtrlResponse<IPremiaLOV[]>>(`/providers/premia/values/banks/`)
      .pipe(tap((response) => this.cacheService.set(this.CACHE_KEYS.PREMIA_BANKS, response, 1440)));
  }

  getPremiaBranches(bank: string): Observable<ICtrlResponse<IPremiaLOV[]>> {
    return this.ctrlHttp.get<ICtrlResponse<IPremiaLOV[]>>(`/providers/premia/values/banks_branches/?bank_code=${bank}`);
  }

  getPremiaAccountTypes(): Observable<ICtrlResponse<IPremiaLOV[]>> {
    const cached = this.cacheService.get(this.CACHE_KEYS.PREMIA_ACCOUNT_TYPES);
    if (cached) {
      return of(cached);
    }

    return this.ctrlHttp
      .get<ICtrlResponse<IPremiaLOV[]>>(`/providers/premia/values/banks_account_types/`)
      .pipe(tap((response) => this.cacheService.set(this.CACHE_KEYS.PREMIA_ACCOUNT_TYPES, response, 1440)));
  }

  getPremiaRelationshipTypes(): Observable<ICtrlResponse<IPremiaLOV[]>> {
    const cached = this.cacheService.get(this.CACHE_KEYS.PREMIA_BANK_RELATIONSHIP_TYPES);
    if (cached) {
      return of(cached);
    }

    return this.ctrlHttp
      .get<ICtrlResponse<IPremiaLOV[]>>(`/providers/premia/values/banks_relationship_types/`)
      .pipe(tap((response) => this.cacheService.set(this.CACHE_KEYS.PREMIA_BANK_RELATIONSHIP_TYPES, response, 1440)));
  }

  loadCASAList(): Observable<ICtrlResponse<ICasaDetails>> {
    const cached = this.cacheService.get(this.CACHE_KEYS.CASA_LIST);
    if (cached) {
      return of(cached);
    }

    return this.ctrlHttp.get(`/casa/combined`).pipe(tap((response) => this.cacheService.set(this.CACHE_KEYS.CASA_LIST, response, 60)));
  }

  async updateCASA(
    corCode: string,
    corDesc: string,
    cobCode: string,
    cobDesc: string,
    nationalityCode: string,
    nationalityDesc: string,
    sourceOfIncomeCode: string,
    sourceOfIncomeDesc: string,
    empCode: string,
    empDesc: string,
    occupation_code: string,
    occupationDesc: string
  ) {
    this.sharedConfig.user.personal_details.country_of_birth_code = cobCode;
    this.sharedConfig.user.personal_details.country_of_birth_description = cobDesc;
    this.sharedConfig.user.personal_details.country_of_residence_code = corCode;
    this.sharedConfig.user.personal_details.country_of_residence_description = corDesc;
    this.sharedConfig.user.personal_details.casa_nationality_code = nationalityCode;
    this.sharedConfig.user.personal_details.casa_nationality_description = nationalityDesc;
    this.sharedConfig.user.personal_details.casa_source_of_income_code = sourceOfIncomeCode;
    this.sharedConfig.user.personal_details.casa_source_of_income_description = sourceOfIncomeDesc;
    this.sharedConfig.user.personal_details.casa_employment_status_code = empCode;
    this.sharedConfig.user.personal_details.casa_employment_status_description = empDesc;
    this.sharedConfig.user.personal_details.casa_occupation_code = occupation_code;
    this.sharedConfig.user.personal_details.casa_occupation_description = occupationDesc;

    this.apiUserService.updatePersonalDetails(this.sharedConfig.user.personal_details).pipe(take(1)).subscribe();
  }

  async setScheduleLinkedAssets(activeSchedules: Array<ISchedule>): Promise<Array<ISchedule>> {
    const insuranceTypes: Array<{ type: AssetType; key: keyof IScheduleInsuredAssets }> = [
      { type: AssetType.Contents, key: 'contents' },
      { type: AssetType.Vehicles, key: 'vehicles' },
      { type: AssetType.Motorcycles, key: 'motorcycles' },
      { type: AssetType.Caravans, key: 'caravans' },
      { type: AssetType.Buildings, key: 'buildings' },
      { type: AssetType.SpecifiedItems, key: 'specified_items' },
      { type: AssetType.AllRisks, key: 'all_risks' },
      { type: AssetType.Vaps, key: 'vaps' },
    ];
    const insuranceItemsPromises = insuranceTypes.map((it) => this.apiPolicyService.getInsuredItems<IInsuredAsset>(activeSchedules, it.type));
    const insuranceItemsResults = await Promise.all(insuranceItemsPromises);
    const insuranceItemsMap = insuranceTypes.reduce(
      (acc, it, index) => {
        acc[it.type] = insuranceItemsResults[index];
        return acc;
      },
      {} as { [type in AssetType]?: IInsuredAsset[] }
    );
    activeSchedules.forEach((schedule) => {
      insuranceTypes.forEach((it) => {
        const insuredItems = insuranceItemsMap[it.type];
        if (insuredItems) {
          (schedule.insured_assets[it.key] as IInsuredAsset[]) = insuredItems.filter((item) => item.schedule.id === schedule.id);
        }
      });
    });
    return activeSchedules;
  }

  // Cache management helper methods
  private invalidateQuoteRequestCaches(quoteRequestId: number): void {
    this.cacheService.clear(`${this.CACHE_KEYS.QUOTE_REQUEST}_${quoteRequestId}`);
    this.cacheService.clear(this.CACHE_KEYS.QUOTE_REQUESTS);
    this.cacheService.clear(this.CACHE_KEYS.ALL_SCHEDULES);
  }

  private invalidateAddressRelatedCaches(): void {
    this.cacheService.clear(this.CACHE_KEYS.ALL_SCHEDULES);
    this.cacheService.clear(this.CACHE_KEYS.QUOTE_REQUESTS);
  }

  clearAllCaches(): void {
    Object.values(this.CACHE_KEYS).forEach((key) => {
      this.cacheService.clearByPrefix(key);
    });
  }
}
